import { useColorMode } from '@vueuse/core';

/**
 * The CSS variable map driving the design system. Per-account theme overrides
 * (from `account.settings.theme`) are spread on top of these defaults at
 * runtime via `applyAccountTheme()`.
 */
export const DEFAULT_THEME_TOKENS: Record<string, string> = {
  '--color-primary': 'oklch(0.55 0.22 280)',
  '--color-primary-foreground': 'oklch(1 0 0)',
  '--color-ring': 'oklch(0.55 0.22 280)',
  '--radius-sm': '6px',
  '--radius-md': '8px',
  '--radius-lg': '12px',
};

export type AccountThemeOverrides = Partial<Record<keyof typeof DEFAULT_THEME_TOKENS, string>> &
  Record<string, string>;

const HEX6 = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

function normalizeHex6(raw: string): string | null {
  const m = HEX6.exec(raw.trim());
  if (!m) return null;
  return `#${m[1]!.toLowerCase()}${m[2]!.toLowerCase()}${m[3]!.toLowerCase()}`;
}

function srgbToLinear(c255: number): number {
  const c = c255 / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance for sRGB hex (0–1). */
export function relativeLuminanceFromHex(hexRaw: string): number | null {
  const hex = normalizeHex6(hexRaw);
  if (!hex) return null;
  const r = srgbToLinear(parseInt(hex.slice(1, 3), 16));
  const g = srgbToLinear(parseInt(hex.slice(3, 5), 16));
  const b = srgbToLinear(parseInt(hex.slice(5, 7), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function primaryForegroundForHex(hex: string): string {
  const L = relativeLuminanceFromHex(hex);
  if (L == null) return 'oklch(0.99 0 0)';
  return L > 0.55 ? 'oklch(0.16 0 0)' : 'oklch(0.99 0 0)';
}

/**
 * CSS variables for account brand color (buttons, focus ring, sidebar mark, etc.).
 */
export function primaryHexToThemeTokens(hexRaw: string, scheme: 'light' | 'dark'): AccountThemeOverrides {
  const hex = normalizeHex6(hexRaw) ?? hexRaw.trim();
  const fg = primaryForegroundForHex(hex);
  if (scheme === 'dark') {
    return {
      '--color-primary': `color-mix(in oklch, ${hex} 88%, white)`,
      '--color-primary-foreground': fg,
      '--color-ring': `color-mix(in oklch, ${hex} 72%, white)`,
    };
  }
  return {
    '--color-primary': hex,
    '--color-primary-foreground': fg,
    '--color-ring': hex,
  };
}

/**
 * Applies the active account primary (#RRGGBB) to `--color-primary*` / `--color-ring`.
 * Pass null/empty to fall back to design tokens in `globals.css`.
 */
export function syncRuntimeAccountTheme(input: {
  primaryHex: string | null | undefined;
  scheme: 'light' | 'dark';
}): void {
  const raw = input.primaryHex?.trim() ?? '';
  if (!raw || !/^#[0-9A-Fa-f]{6}$/.test(raw)) {
    applyAccountTheme(null);
    return;
  }
  applyAccountTheme(primaryHexToThemeTokens(raw, input.scheme));
}

/**
 * Applies a theme override to the document root. Pass `null` to reset.
 */
export function applyAccountTheme(overrides: AccountThemeOverrides | null): void {
  const root = document.documentElement;
  for (const key of Object.keys(DEFAULT_THEME_TOKENS)) {
    root.style.removeProperty(key);
  }
  if (!overrides) return;
  for (const [k, v] of Object.entries(overrides)) {
    if (v) root.style.setProperty(k, v);
  }
}

/**
 * Vueuse `useColorMode` configured to mirror our `<html data-theme>` /
 * `.dark` switch. Persists to localStorage under `scala-watch:theme`.
 */
export function useTheme() {
  return useColorMode({
    storageKey: 'scala-watch:theme',
    selector: 'html',
    attribute: 'data-theme',
    modes: { light: 'light', dark: 'dark' },
    onChanged(mode, defaultHandler) {
      defaultHandler(mode);
      const root = document.documentElement;
      if (mode === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');
    },
  });
}
